/**
 * HR-portal FTUE — People CSV import wizard (`/import`).
 *
 * Owns the four on-page coachmarks of the `portal-hr-activation` tour that walk
 * an HR admin through the People CSV import wizard:
 *  - the drag-drop dropzone and the "Download Blank Template" button, both shown
 *    while the wizard is on its first step (`step === 'upload'`)
 *  - the diff/preview table and the "Import N people" button, both shown once
 *    the wizard reaches its review step (`step === 'review'`)
 *
 * Mounted from `PortalImportPage`. The layout hook (`use-portal-hr-ftue`) owns
 * the flow start and the nav step that leads here; this hook only adds the four
 * follow-up steps (orders x2-x5) so each `condition` can close over the page's
 * live wizard state, matching the job-seeker pattern (`use-opportunities-ftue`,
 * `use-learn-ftue`).
 *
 * The dropzone and the review table are LARGE regions, so neither is
 * `dismissOnAction` — a stray click inside them must not complete the step
 * (mirrors `use-resume-upload-ftue`). The way forward for those two is the "Got
 * it" ack, or the re-eval effect below auto-advancing once the wizard moves past
 * the step's screen. The template-download button and the confirm button are
 * each one small button, so both ARE `dismissOnAction`: clicking them is the
 * admin acting on the intent (mirrors `internalJobsAdd`).
 */

import { getStep, registerSteps, useFtueAnchor, useFtueStore } from '@/ftue'
import { useEffect, useRef } from 'react'
import {
  PORTAL_HR_ACTIVATION_FLOW,
  PORTAL_HR_ANCHOR,
  PORTAL_HR_ORDER,
  PORTAL_HR_STEP,
  onPortalPath,
  portalStep,
  reevaluatePortalHrFlow,
} from './flow'

export interface PortalImportFtueArgs {
  /** The import wizard is on its first step. The page passes `step === 'upload'`;
   *  only then are the dropzone and the template button rendered and those two
   *  steps a candidate. */
  uploadStep: boolean
  /** The import wizard is on its review step. The page passes `step === 'review'`;
   *  only then are the review table and the confirm button rendered and those
   *  two steps a candidate. */
  reviewStep: boolean
}

const IMPORT_PATH = '/import'

/** This hook's own step ids, read by the re-eval effect below so it only ever
 *  retires a step this hook registered, never one owned by another hook on the
 *  shared flow. */
const OWN_STEP_IDS: readonly string[] = [
  PORTAL_HR_STEP.importDropzone,
  PORTAL_HR_STEP.importTemplate,
  PORTAL_HR_STEP.importReview,
  PORTAL_HR_STEP.importConfirm,
]

export function usePortalImportFtue({ uploadStep, reviewStep }: PortalImportFtueArgs) {
  const dropzoneAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.importDropzone)
  const templateAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.importTemplateCta)
  const reviewTableAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.importReviewTable)
  const confirmAnchor = useFtueAnchor(PORTAL_HR_ANCHOR.importConfirmCta)

  // Live wizard state for the step `condition`s (registered once below, so the
  // closures must read the current values rather than the mount-time snapshot).
  const uploadStepRef = useRef(uploadStep)
  uploadStepRef.current = uploadStep
  const reviewStepRef = useRef(reviewStep)
  reviewStepRef.current = reviewStep

  // Register the four on-page steps once. Registration is idempotent (keyed by
  // id) and does not itself trigger evaluation.
  useEffect(() => {
    registerSteps([
      portalStep({
        id: PORTAL_HR_STEP.importDropzone,
        phase: PORTAL_HR_STEP.importDropzone,
        order: PORTAL_HR_ORDER.importDropzone,
        trigger: { type: 'immediate' },
        // Only a candidate on the import wizard's first step, where the dropzone
        // is rendered. Off `/import` (or past the upload step) it is skipped so an
        // immediate step whose anchor is absent never blocks the rest of the flow
        // (see the note in `use-resume-upload-ftue`).
        condition: () => onPortalPath(IMPORT_PATH) && uploadStepRef.current,
        anchorId: PORTAL_HR_ANCHOR.importDropzone,
        placement: 'bottom',
        allowAnchorInteraction: true,
        // The dropzone is a large region: no `dismissOnAction` (a stray click must
        // not complete the step). Forward = the "Ok" ack, or the re-eval
        // effect auto-advances once the admin uploads and the wizard moves on.
        content: {
          body: 'Drop your people CSV here - name, email, department, and role. Pelgo matches the columns for you.',
          dismissLabel: 'Ok',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.importTemplate,
        phase: PORTAL_HR_STEP.importTemplate,
        order: PORTAL_HR_ORDER.importTemplate,
        trigger: { type: 'immediate' },
        // Same screen as the dropzone above — the FormatGuide sidecar card (and
        // its "Download Blank Template" button) only renders on the wizard's
        // first step.
        condition: () => onPortalPath(IMPORT_PATH) && uploadStepRef.current,
        anchorId: PORTAL_HR_ANCHOR.importTemplateCta,
        placement: 'bottom',
        // One small button that starts a download (no nav): clicking it is
        // intent, so complete the step there rather than leaving the coachmark
        // hanging (mirrors `internalJobsAdd`).
        dismissOnAction: true,
        content: {
          body: 'Not sure what columns we expect? Download a blank template here.',
          dismissLabel: 'Ok',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.importReview,
        phase: PORTAL_HR_STEP.importReview,
        order: PORTAL_HR_ORDER.importReview,
        trigger: { type: 'immediate' },
        // Only a candidate once the wizard has a preview to show.
        condition: () => onPortalPath(IMPORT_PATH) && reviewStepRef.current,
        anchorId: PORTAL_HR_ANCHOR.importReviewTable,
        placement: 'bottom',
        // The diff table is a large region: no `dismissOnAction`, same reasoning
        // as the dropzone above. Forward = the "Ok" ack, or the re-eval
        // effect once the admin confirms or backs out of review.
        content: {
          body: 'Review each row before anything saves - add, update, or error is called out per person.',
          dismissLabel: 'Ok',
        },
      }),
      portalStep({
        id: PORTAL_HR_STEP.importConfirm,
        phase: PORTAL_HR_STEP.importConfirm,
        order: PORTAL_HR_ORDER.importConfirm,
        trigger: { type: 'immediate' },
        // Same screen as the review table above.
        condition: () => onPortalPath(IMPORT_PATH) && reviewStepRef.current,
        anchorId: PORTAL_HR_ANCHOR.importConfirmCta,
        placement: 'bottom',
        // One small button that commits the import (no nav): clicking it is
        // intent, so complete the step there.
        dismissOnAction: true,
        content: {
          body: 'Nothing is saved until you click here.',
          dismissLabel: 'Ok',
        },
      }),
    ])

    // Registration alone does not trigger evaluation — start the flow so the
    // engine re-picks the candidate now these steps exist. Idempotent; the
    // layout hook (`use-portal-hr-ftue`) owns the real start logic.
    reevaluatePortalHrFlow()
  }, [])

  // Re-evaluate when the wizard step changes (a plain re-render doesn't re-run the
  // engine). If the active step is one of this hook's own four and its
  // `condition` has flipped false (the wizard moved to a different screen) and it
  // hasn't been acknowledged yet, mark it seen so the tour advances cleanly
  // instead of the coachmark just vanishing. Otherwise just re-pick the
  // candidate. Scoped to this hook's own step ids so it never retires a step
  // owned by another hook on the shared flow. Mirrors `use-learn-ftue`.
  // biome-ignore lint/correctness/useExhaustiveDependencies: uploadStep/reviewStep intentionally re-trigger evaluation; the effect reads live state via getState()
  useEffect(() => {
    const { activeStepId, seen, complete } = useFtueStore.getState()
    if (activeStepId && OWN_STEP_IDS.includes(activeStepId)) {
      const s = getStep(PORTAL_HR_ACTIVATION_FLOW, activeStepId)
      if (s?.condition && !s.condition() && !seen.has(activeStepId)) {
        complete(activeStepId)
        return
      }
    }
    reevaluatePortalHrFlow()
  }, [uploadStep, reviewStep])

  return {
    /** Spread onto the drag-drop dropzone `<div>` on the wizard's first step. */
    dropzoneAnchor,
    /** Attach to the "Download Blank Template" `<Button>` inside `FormatGuide`. */
    templateAnchor,
    /** Spread onto the review/diff table's wrapping `<div>`. */
    reviewTableAnchor,
    /** Attach to the "Import N people" `<Button>`. */
    confirmAnchor,
  }
}
